// react core
import { useState } from "react";

// material design
import Box from "@mui/material/Box";
import Grow from "@mui/material/Grow";
import Fade from "@mui/material/Fade";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";

// fonts and icons
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExplosion as iconLaunchedDuo } from "@fortawesome/pro-duotone-svg-icons";
import { faExplosion as iconLaunchedThin } from "@fortawesome/pro-thin-svg-icons";
import { faCircle as iconCircleDuo } from "@fortawesome/pro-duotone-svg-icons";
import { faCircle as iconCircleReg } from "@fortawesome/pro-solid-svg-icons";
import { faAtomAlt as iconEventuators } from "@fortawesome/pro-thin-svg-icons";
import { faPeopleGroup as iconPeople } from "@fortawesome/pro-thin-svg-icons";
import { faSignalStream as iconChange } from "@fortawesome/pro-thin-svg-icons";
import { faUsersViewfinder as iconTribeFinder } from "@fortawesome/pro-thin-svg-icons";
import { faUserPlus as iconJoin } from "@fortawesome/pro-thin-svg-icons";
import { faChevronLeft as iconNavLeft } from "@fortawesome/pro-thin-svg-icons";
import { faChevronRight as iconNavRight } from "@fortawesome/pro-thin-svg-icons";
import { faXmark as iconClose } from "@fortawesome/pro-thin-svg-icons";
import { orange } from "@mui/material/colors/";

// import { faInfoCircle as iconInfo } from "@fortawesome/pro-thin-svg-icons";
// import { faFaceSmileRelaxed as iconRelax } from "@fortawesome/pro-thin-svg-icons";
// import { faBan as iconBan } from "@fortawesome/pro-thin-svg-icons";
// import { faPhotoFilm as iconMedia } from "@fortawesome/pro-duotone-svg-icons";
// import { faDatabase as iconData } from "@fortawesome/pro-duotone-svg-icons";
// import { faHeart as iconMission } from "@fortawesome/pro-thin-svg-icons";

// entzy context and services
import configEntzy from "components/config/ConfigEntzy";
import Hero from "components/utils/custom/CustomHero";
import { ActionButton } from "components/utils/common/CommonButtons";
import { scrollSnapTop } from "models/Tools";

// entzy assets
import imageHero0 from "assets/heros/community-signal.png";
import imageHero1 from "assets/heros/tribe-finder-people.png";
import imageHero2 from "assets/heros/events-bar.png";
import imageHero3 from "assets/heros/calendar-confirmed.png";
import imageHero4 from "assets/heros/tribe-finder-outdoor.png";

function AboutExperiment(props) {
  const user = props.user;
  const navigate = props.navigate;
  const drawerConnectToggle = props.drawerConnectToggle;

  const [stepId, setStepId] = useState(props.stepId ? props.stepId : 0);
  const [preview, setPreview] = useState(props.loadPreview ? true : false);

  const handlePreview = (view) => {
    setPreview(view);
    setStepId(0);
  };

  const handleStep = (stepId) => {
    if (preview) {
      handlePreview(false);
    }
    setStepId(stepId);
  };

  // constants
  const steps = [
    {
      id: 0,
      title: "Introduction",
      menu: false,
      hero: {
        image: imageHero0,
        eyebrow: "About Entzy",
        title: "Build Community",
        // title: "Social? For Real.",
        // title: "A Real Social Network",
        // title: "Real Sociable",
        subtitle: "Put your signal up",
        // Build close friendships over time, in your own area, with simple repeat meets.",
        //  Help us help you find community.
        bulletList: [
          {
            text: "Find your tribe",
            onClick: () => handleStep(1),
          },
          {
            text: "Join eventuators",
            onClick: () => handleStep(2),
          },
          {
            text: "Meet repeatedly",
            onClick: () => handleStep(3),
          },
        ],
      },
      body: {
        icon: iconChange,
        title: "Raise a signal for change",
        textList: [
          "Technology is making us more isolated. Artifically Isolated.",
          "Rediscover the joy of finding real people, in your local area.",
          "Communities and long-term, group friendships are built over time, with simple repeat meets. Coffee, beer, wine, walk, park, movie, chat, fix something together - does not matter. Shift the activity to the background and focus on finding your groups.",
          "To get involved, add your signal to the map to tribalize. No AI, no location tracking, no need for content, no ads.",
        ],
        primaryAction: {
          label: "We need you",
          onClick: () => handleStep(1),
        },
        secondaryAction: user.connected
          ? null
          : {
              label: "Connect",
              onClick: () => drawerConnectToggle(),
              variant: "outlined",
            },
      },
    },
    {
      id: 1,
      title: "Find your tribe",
      menu: true,
      hero: {
        image: imageHero1,
        eyebrow: "Step One",
        title: "Find your Tribe",
        subtitle: "Broadcast yourself and pass it on!",
      },
      body: {
        icon: iconTribeFinder,
        title: "Put your signal up to meet",
        // bulletList: [
        //   { text: "Set your Area" },
        //   { text: "Broadcast" },
        //   { text: "Get Ready" },
        // ],
        textList: [
          "To kick start a community movement, we need to find out who is around us and say hello to our neighbors.",
          "Broadcast your interest to meet people in your local area, by simply setting a preferred zone on the map.",
          "That's it, to kick things off.",
          "Entzy is 'set and forget' until something alerts you - being offline is the priority. We'll let you know when others start broadcasting nearby.",
          "Next, let's see how eventuators can quietly launch group meets for you. Without organizer overhead. Repeatedly.",
        ],
        backAction: {
          label: "Back",
          onClick: () => handleStep(0),
        },
        primaryAction: {
          label: "Next",
          onClick: () => handleStep(2),
        },
        // secondaryAction: {
        //   label: "Show me",
        //   onClick: () => {
        //     scrollSnapTop();
        //     navigate("/tribe");
        //   },
        // },
      },
    },
    {
      id: 2,
      title: "Join eventuators",
      menu: true,
      hero: {
        image: imageHero2,
        eyebrow: "Step Two",
        title: "Join Eventuators",
        // subtitle:
        //   "Join or create eventuators, that launch simple group meets on demand, whenever you're available",
        // subtitle: "Eventuate activity in groups that launch on demand",
        subtitle: "Eventuate in groups that launch on demand",
      },
      body: {
        icon: iconEventuators,
        title: "Create to Eventuate",
        // bulletList: [
        //   { text: "Locate" },
        //   { text: "Communicate" },
        //   { text: "Eventuate" },
        // ],
        textList: [
          "So how do you meet new people and maintain friendships in a way that feels natural?",
          "Events apps tend to be overkill, or too transient and city-centric. Dating apps, or any one-on-one meets, are often too forced.",
          "Eventuators instead bring small groups of people together, with minimal effort, automatically and repeatedly, based on availability.",
          "Set your group size, interests and available dates, then wait for activity to arrive in your calendar.",
          "Experiment creating or joining different public groups until you find your regular tribe, then move to private eventuators with your crew.",
          "Mobilizing in small groups in your area softens the experience of meeting new people.",
        ],
        backAction: {
          label: "Back",
          onClick: () => handleStep(1),
        },
        primaryAction: {
          label: "Next",
          onClick: () => handleStep(3),
        },
        secondaryAction: user.connected
          ? null
          : {
              label: "Show me",
              onClick: () => {
                scrollSnapTop();
                navigate("/eventuators");
              },
            },
      },
    },
    {
      id: 3,
      title: "Meet repeatedly",
      menu: true,
      hero: {
        image: imageHero3,
        eyebrow: "Step Three",
        title: "Meet Repeatedly",
        // subtitle:
        //   "Let your calendar track matches and schedule meets based on your preferences, so that you can build friendships over time",
        subtitle: "Build friendships over time with simple, repeat meets",
      },
      body: {
        icon: iconPeople,
        title: "Automatically Meet and Repeat",
        // bulletList: [{ text: "Match" }, { text: "Meet" }],
        textList: [
          "Allow your calendar to track matches, schedule meets and alert you of new activity in the area.",
          "Group meets can be anything, but the default options are simply finding an excuse to get together. The focus is on spending regular time in good company, rather than elaborate plans or the event itself.",
          "The goal for your calendar is to build friendships over time, through regular connects, without pressure on any one organizer.",
          "Over time, more and more of your local area should light up with people you know.",
          "Ready to set your signal?",
        ],
        backAction: {
          label: "Back",
          onClick: () => handleStep(2),
        },
        primaryAction: {
          label: "Get Involved",
          onClick: () => handleStep(4),
        },
        // secondaryAction: {
        //   label: "Show me",
        //   onClick: () => {
        //     scrollSnapTop();
        //     navigate("/calendar");
        //   },
        // },
      },
    },
    {
      id: 4,
      title: "Get Involved",
      menu: false,
      hero: {
        image: imageHero4,
        eyebrow: "We need you",
        title: "Get Involved",
        subtitle: "Help us build community at the local level",
      },
      body: {
        icon: iconJoin,
        title: "Join the Movement",
        // bulletList: [{ text: "Make something happen" }],
        textList: [
          "Entzy is a grass roots project to build new in-person connections and community at the local level.",
          "We need you to make it work. Join and broadcast your area of interest and let's see what momemtum builds. Pass the message on.",
          "Things may not happen overnight, but we'll do everything we can to continually grow activity and find people in your area. Eventuate for free or generate your own revenue with chargeable activities. These activities will directly support the project.",
          // "If you are based in Switzerland or passing through, let's have a coffee. Jump into our local catch up eventuator below.",
        ],
        backAction: { label: "Back", onClick: () => handleStep(3) },
        primaryAction: user.connected
          ? null
          : {
              label: "Connect",
              onClick: () => drawerConnectToggle(),
            },
        // secondaryAction: {
        //   label: "Catch Uppa Cuppa",
        //   onClick: () => {
        //     scrollSnapTop();
        //     navigate("/ch/catch_uppa_cuppa");
        //   },
        // },
      },
    },
  ];
  return (
    <Box
      className="box-default"
      sx={{
        p: configEntzy.APP_SPACING_MD,
        borderRadius: configEntzy.BORDER_SIZE_XL,
      }}
    >
      <Fade in={true} timeout={1000}>
        <Box className="box-default">
          <Box
            className="box-default text-right"
            hidden={!props.loadPreview || preview}
          >
            <Typography variant="subtitle2">
              <span
                className="action-pointer"
                onClick={() => handlePreview(true)}
              >
                <FontAwesomeIcon icon={iconClose} size="2x" fixedWidth />
              </span>
            </Typography>
          </Box>
          <Box className="box-default" hidden={preview}>
            <Box className="box-default">
              <Box
                component="span"
                className="fa-layers fa-fw fa-6x action-pointer"
              >
                <FontAwesomeIcon
                  icon={iconLaunchedDuo}
                  style={{ color: orange[500] }}
                />
                <FontAwesomeIcon
                  icon={iconLaunchedThin}
                  style={{ color: orange[500] }}
                />
                <FontAwesomeIcon
                  icon={iconCircleDuo}
                  transform="shrink-3"
                  style={{ color: orange[500] }}
                />
                <FontAwesomeIcon
                  icon={iconCircleDuo}
                  transform="shrink-6"
                  style={{ color: orange[700] }}
                />
                <FontAwesomeIcon
                  icon={iconCircleReg}
                  transform="shrink-9"
                  style={{ color: orange[200] }}
                />
                <FontAwesomeIcon
                  icon={iconEventuators}
                  transform="shrink-12"
                  style={{ color: orange[900] }}
                />
              </Box>
            </Box>
            <Box
              className="box-default"
              sx={{
                pt: configEntzy.APP_SPACING_MD,
                pb: configEntzy.APP_SPACING_MD2X,
              }}
            >
              {user.connected ? (
                <Box className="box-default">
                  {/* <Typography variant="h6" color="white">
                    The Entzy Movement
                  </Typography> */}
                  <Typography
                    variant="h6"
                    color="white"
                    sx={{ mt: configEntzy.APP_SPACING_SM }}
                  >
                    Build community with simple repeat meets
                  </Typography>
                </Box>
              ) : (
                <Box className="box-default">
                  <Typography variant="h2" color="white">
                    Join the Entzy Movement
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ mt: configEntzy.APP_SPACING_SM }}
                  >
                    Help us build community with simple repeat meets
                  </Typography>
                </Box>
              )}
            </Box>
            <Box
              className="box-default"
              sx={{
                p: configEntzy.APP_SPACING_MD,
              }}
            >
              <Divider />
            </Box>
          </Box>
          <Box
            className="box-default"
            sx={{
              pt: configEntzy.APP_SPACING_MD,
              pb: configEntzy.APP_SPACING_MD,
            }}
          >
            <Box
              className="box-default action-pointer"
              sx={{
                width: preview ? "0%" : "5%",
                visibility: stepId <= 0 || preview ? "hidden" : "visible",
              }}
              onClick={() => handleStep(stepId > 0 ? stepId - 1 : 0)}
            >
              <FontAwesomeIcon icon={iconNavLeft} size="2x" fixedWidth />
            </Box>
            <Box
              className="box-default"
              sx={{
                width: preview ? "100%" : "90%",
              }}
            >
              <Stepper activeStep={stepId - 1} alternativeLabel>
                {steps
                  .filter((step) => step.menu)
                  .map((step) => (
                    <Step
                      key={step.id}
                      onClick={() => handleStep(step.id)}
                      className="action-pointer"
                    >
                      <StepLabel>{step.title}</StepLabel>
                    </Step>
                  ))}
              </Stepper>
            </Box>
            <Box
              className="box-default action-pointer"
              sx={{
                width: preview ? "0%" : "5%",
                visibility:
                  stepId >= steps.length - 1 || preview ? "hidden" : "visible",
              }}
              onClick={() =>
                handleStep(stepId < steps.length - 1 ? stepId + 1 : stepId)
              }
              hidden={preview}
            >
              <FontAwesomeIcon icon={iconNavRight} size="2x" fixedWidth />
            </Box>
          </Box>
          <Box className="box-default" hidden={preview}>
            <Box
              className="box-default"
              sx={{
                p: configEntzy.APP_SPACING_MD,
              }}
            >
              <Divider />
            </Box>
            <Box
              className="box-default"
              sx={{
                // minHeight: 1000,
                // mb: configEntzy.APP_SPACING_HL,
                p: configEntzy.APP_SPACING_MD,
              }}
            >
              {steps.map((step) =>
                step.id === stepId ? (
                  <Grow in={true} timeout={1000} key={step.id}>
                    <Box className="box-default">
                      <Box
                        className="box-default nooverflow"
                        sx={{
                          borderRadius: configEntzy.BORDER_SIZE_XL,
                        }}
                      >
                        <Hero
                          image={step.hero.image}
                          eyebrow={step.hero.eyebrow}
                          title={step.hero.title}
                          subtitle={step.hero.subtitle}
                          bulletList={step.hero.bulletList}
                          align="center"
                          primaryAction={step.hero.primaryAction}
                          secondaryAction={step.hero.secondaryAction}
                        />
                      </Box>
                      <Box
                        className="box-default bg-black-t50 shadow-darkest"
                        sx={{
                          mt: configEntzy.APP_SPACING_MD,
                          p: configEntzy.APP_SPACING_MD3X,
                          borderRadius: configEntzy.BORDER_SIZE_XL,
                        }}
                      >
                        <Box className="box-default text-left">
                          <Typography variant="h3" color="primary">
                            <FontAwesomeIcon
                              icon={step.body.icon}
                              size="2x"
                              transform="left-1"
                            />
                          </Typography>
                          <Typography
                            variant="h6"
                            color="primary"
                            sx={{ mt: configEntzy.APP_SPACING_MD2X }}
                          >
                            {step.body.title}
                          </Typography>
                        </Box>
                        {step.body.bulletList &&
                          step.body.bulletList.length > 0 && (
                            <Box
                              className="box-default text-left"
                              sx={{ mt: configEntzy.APP_SPACING_MD }}
                            >
                              {step.body.bulletList.map((item, index) => (
                                <Box
                                  key={index}
                                  className="box-inline"
                                  sx={{
                                    // width: 100 / step.body.bulletList.length + "%",
                                    pl: configEntzy.APP_SPACING_SM2X,
                                    pr: configEntzy.APP_SPACING_SM2X,
                                    borderLeft: 1,
                                    borderColor: "rgba(255, 255, 255, 0.2)",
                                  }}
                                >
                                  <Typography
                                    variant="subtitle2"
                                    color="primary"
                                    noWrap
                                  >
                                    {item.text}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          )}
                        {step.body.textList && step.body.textList.length > 0 && (
                          <Box
                            className="box-default text-left"
                            sx={{ mt: configEntzy.APP_SPACING_MD2X }}
                          >
                            {step.body.textList.map((text, index) => (
                              <Typography
                                key={index}
                                variant="subtitle2"
                                color="white"
                                sx={{
                                  mt:
                                    index === 0
                                      ? 0
                                      : configEntzy.APP_SPACING_MD2X,
                                }}
                              >
                                {text}
                              </Typography>
                            ))}
                          </Box>
                        )}
                        <Box
                          className="box-default text-right"
                          sx={{ mt: configEntzy.APP_SPACING_MD3X }}
                        >
                          {step.body.backAction && (
                            <Box className="box-inline left">
                              <ActionButton
                                size="small"
                                text={step.body.backAction.label}
                                onClick={step.body.backAction.onClick}
                                variant="outlined"
                              />
                            </Box>
                          )}
                          {step.body.secondaryAction && (
                            <Box
                              className="box-inline"
                              sx={{ mr: configEntzy.APP_SPACING_MD }}
                            >
                              <ActionButton
                                size="small"
                                text={step.body.secondaryAction.label}
                                onClick={step.body.secondaryAction.onClick}
                                variant="outlined"
                                sx={{ ml: configEntzy.APP_SPACING_SM }}
                              />
                            </Box>
                          )}
                          {step.body.primaryAction && (
                            <Box
                              className="box-inline"
                              sx={{
                                p: configEntzy.APP_SPACING_XS,
                              }}
                            >
                              <ActionButton
                                size="small"
                                text={step.body.primaryAction.label}
                                onClick={step.body.primaryAction.onClick}
                                variant="contained"
                              />
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </Grow>
                ) : null,
              )}
            </Box>
          </Box>
        </Box>
      </Fade>
    </Box>
  );
}

export default AboutExperiment;
